import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Car, GripVertical } from "lucide-react";

// 13-state pipeline columns + colors
export const PIPELINE_COLUMNS = [
  { key: "new_request", label: "New Request", accent: "slate" },
  { key: "vehicle_info_received", label: "Vehicle Info", accent: "amber" },
  { key: "ro_insurance_pending", label: "RO / Insurance Pending", accent: "orange" },
  { key: "estimate_created", label: "Estimate Created", accent: "blue" },
  { key: "waiting_approval", label: "Waiting Approval", accent: "cyan" },
  { key: "scheduled", label: "Scheduled", accent: "indigo" },
  { key: "in_progress", label: "In Progress", accent: "purple" },
  { key: "calibration_completed", label: "Calibration Done", accent: "teal" },
  { key: "report_generated", label: "Report Generated", accent: "emerald" },
  { key: "invoiced", label: "Invoiced", accent: "indigo" },
  { key: "paid", label: "Paid", accent: "emerald" },
  { key: "closed", label: "Closed", accent: "slate" },
];

const accentClass = {
  slate: "border-slate-700/60 bg-slate-800/30 text-slate-300",
  amber: "border-amber-500/30 bg-amber-500/5 text-amber-200",
  orange: "border-orange-500/30 bg-orange-500/5 text-orange-200",
  blue: "border-blue-500/30 bg-blue-500/5 text-blue-200",
  cyan: "border-cyan-500/30 bg-cyan-500/5 text-cyan-200",
  indigo: "border-indigo-500/30 bg-indigo-500/5 text-indigo-200",
  purple: "border-purple-500/30 bg-purple-500/5 text-purple-200",
  teal: "border-teal-500/30 bg-teal-500/5 text-teal-200",
  emerald: "border-emerald-500/30 bg-emerald-500/5 text-emerald-200",
};

export default function KanbanBoard({ jobs, onUpdated }) {
  const [draggingId, setDraggingId] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  // Group jobs by status (default any unknown → new_request bucket)
  const byStatus = {};
  for (const col of PIPELINE_COLUMNS) byStatus[col.key] = [];
  for (const j of jobs) {
    const s = j.status || "new_request";
    if (byStatus[s]) byStatus[s].push(j);
    else byStatus[s] = [j];
  }

  const onDragStart = (e, jobId) => {
    setDraggingId(jobId);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", jobId);
  };

  const onDragOver = (e, status) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOver !== status) setDragOver(status);
  };

  const onDrop = async (e, newStatus) => {
    e.preventDefault();
    setDragOver(null);
    const jobId = e.dataTransfer.getData("text/plain") || draggingId;
    setDraggingId(null);
    if (!jobId) return;
    const current = jobs.find((j) => j.id === jobId);
    if (!current || current.status === newStatus) return;
    // Optimistic UI: call parent callback after successful PATCH
    try {
      await api.patch(`/jobs/${jobId}`, { status: newStatus });
      toast.success(`Moved to ${newStatus.replace(/_/g, " ")}`);
      onUpdated && onUpdated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update status");
    }
  };

  return (
    <div className="overflow-x-auto pb-3" data-testid="kanban-board">
      <div className="flex gap-3 min-w-max">
        {PIPELINE_COLUMNS.map((col) => {
          const isOver = dragOver === col.key;
          const list = byStatus[col.key] || [];
          return (
            <div
              key={col.key}
              data-testid={`kanban-col-${col.key}`}
              onDragOver={(e) => onDragOver(e, col.key)}
              onDragLeave={() => setDragOver(null)}
              onDrop={(e) => onDrop(e, col.key)}
              className={`w-72 shrink-0 rounded-xl border bg-[#0B1120] flex flex-col ${
                isOver ? "border-blue-500/60 ring-2 ring-blue-500/30" : "border-slate-800"
              }`}
            >
              <div className={`px-3 py-2.5 border-b border-slate-800 flex items-center justify-between rounded-t-xl ${accentClass[col.accent]}`}>
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold truncate">{col.label}</div>
                <span className="font-mono text-xs bg-black/30 px-1.5 py-0.5 rounded">{list.length}</span>
              </div>
              <div className="p-2 space-y-2 min-h-[200px] max-h-[70vh] overflow-y-auto">
                {list.length === 0 && (
                  <div className="text-[10px] text-slate-600 text-center py-6 border border-dashed border-slate-800 rounded">
                    Drop here
                  </div>
                )}
                {list.map((j) => (
                  <div
                    key={j.id}
                    draggable
                    onDragStart={(e) => onDragStart(e, j.id)}
                    onDragEnd={() => { setDraggingId(null); setDragOver(null); }}
                    data-testid={`kanban-card-${j.id}`}
                    className={`group rounded-lg border bg-slate-900/50 p-3 cursor-grab active:cursor-grabbing hover:border-blue-500/50 transition-colors ${
                      draggingId === j.id ? "opacity-40 border-blue-500/50" : "border-slate-800"
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <GripVertical className="w-3.5 h-3.5 text-slate-600 mt-0.5 shrink-0 group-hover:text-slate-400" />
                      <div className="flex-1 min-w-0">
                        <Link to={`/jobs/${j.id}`} className="block hover:text-blue-300">
                          <div className="font-mono text-[11px] text-blue-300 truncate">{j.vin}</div>
                          <div className="text-xs text-slate-200 mt-1 flex items-center gap-1.5">
                            <Car className="w-3 h-3 text-slate-500" />
                            <span className="truncate">{j.vehicle_year} {j.vehicle_make} {j.vehicle_model}</span>
                          </div>
                          <div className="text-[10px] text-slate-400 mt-1.5 truncate">{j.customer_name}</div>
                          {j.body_shop_name && (
                            <div className="text-[10px] text-slate-500 truncate">{j.body_shop_name}</div>
                          )}
                          {j.insurance_company && (
                            <Badge className="bg-slate-800/60 border border-slate-700 text-slate-400 text-[9px] mt-1.5">{j.insurance_company}</Badge>
                          )}
                        </Link>
                        <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-800/60">
                          <span className="text-[10px] text-slate-500 font-mono">#{j.id.slice(0, 8)}</span>
                          {j.total_amount > 0 && (
                            <span className="text-[10px] font-mono text-emerald-400">${j.total_amount.toFixed(0)}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
